import apiClient from './client';
import type { RegimeIva } from './fatture';

// Attributi CER (#130): obbligatori (entrambi) per attivare un CER.
export type Tipologia = 'urbano' | 'speciale';
export type Classificazione = 'non_pericoloso' | 'pericoloso';

export interface CerAttivo {
  id?: string;
  cer: string;
  nome_descrittivo: string;
  categoria_form: string | null;
  tipologia: Tipologia | null;
  classificazione: Classificazione | null;
  attivo?: boolean;
  note: string | null;
}

export async function getCerAttivi(categoria?: string): Promise<CerAttivo[]> {
  const params: Record<string, string> = {};
  if (categoria) params['categoria'] = categoria;
  const { data } = await apiClient.get<CerAttivo[]>('/api/settings/cer-attivi', { params });
  return data;
}

// Gestione admin: include anche i CER disattivati (solo_attivi=false).
export async function getCerAttiviGestione(): Promise<CerAttivo[]> {
  const { data } = await apiClient.get<CerAttivo[]>('/api/settings/cer-attivi', {
    params: { solo_attivi: false },
  });
  return data;
}

export async function createCerAttivo(body: { cer: string; nome_descrittivo: string; categoria_form?: string | null; tipologia?: Tipologia | null; classificazione?: Classificazione | null; note?: string | null }): Promise<CerAttivo> {
  const { data } = await apiClient.post<CerAttivo>('/api/settings/cer-attivi', body);
  return data;
}

export async function updateCerAttivo(id: string, body: { cer?: string; nome_descrittivo?: string; categoria_form?: string | null; tipologia?: Tipologia | null; classificazione?: Classificazione | null; note?: string | null; attivo?: boolean }): Promise<CerAttivo> {
  const { data } = await apiClient.patch<CerAttivo>(`/api/settings/cer-attivi/${id}`, body);
  return data;
}

// ── CER End of Waste ───────────────────────────────────────────────────────────
export interface CerEow {
  id: string;
  cer: string;
  nome_descrittivo: string | null;
  attivo: boolean;
  created_at: string;
  updated_at: string;
}

export async function getCerEow(soloAttivi = false): Promise<CerEow[]> {
  const { data } = await apiClient.get<CerEow[]>('/api/settings/cer-eow', {
    params: { solo_attivi: soloAttivi },
  });
  return data;
}

export async function createCerEow(body: { cer: string; nome_descrittivo?: string | null }): Promise<CerEow> {
  const { data } = await apiClient.post<CerEow>('/api/settings/cer-eow', body);
  return data;
}

export async function updateCerEow(id: string, body: { cer?: string; nome_descrittivo?: string | null; attivo?: boolean }): Promise<CerEow> {
  const { data } = await apiClient.patch<CerEow>(`/api/settings/cer-eow/${id}`, body);
  return data;
}

// ── CER disponibili per tipologia area (R13/EoW/RP/DT=unione) ───────────────────
// V48: ogni RIGA porta id + fonte (l'area ammette la riga, non il codice).
export type FonteCerArea = 'attivo' | 'eow' | 'rifiuto';
export interface CerPerTipo {
  id: string;
  cer: string;
  nome_descrittivo: string | null;
  fonte: FonteCerArea;
}

export async function getCerPerTipoArea(tipo: string): Promise<CerPerTipo[]> {
  const { data } = await apiClient.get<CerPerTipo[]>('/api/settings/cer-per-tipo-area', {
    params: { tipo },
  });
  return data;
}

export interface CategoriaForm {
  codice: string;
  nome_visualizzato: string;
  ordine: number | null;
  attiva: boolean;
  descrizione: string | null;
}

// Le categorie sono già ordinate dal backend (ordine NULLS LAST, poi codice).
// solo_attive=true di default: i dropdown mostrano solo le categorie selezionabili.
export async function getCategorieForm(soloAttive = true): Promise<CategoriaForm[]> {
  const { data } = await apiClient.get<CategoriaForm[]>('/api/settings/categorie-form', {
    params: { solo_attive: soloAttive },
  });
  return data;
}

// ── Categorie: prezzo + direzione di default (#NEW-11/12) ───────────────────────
// Set FISSO (no create/delete): solo prezzo + direzione (+ attiva) modificabili.
export type TipoOperazione = 'servizio' | 'acquisto';

export interface CategoriaSettings {
  categoria: string;             // codice
  label: string;                 // nome_visualizzato
  tariffa_eur_kg: number | null;
  tipo_operazione_default: TipoOperazione;
  iva_default: number;           // aliquota IVA % di default per le righe-da-conferimento
  attiva: boolean;
}

export async function getCategorieSettings(): Promise<CategoriaSettings[]> {
  const { data } = await apiClient.get<CategoriaSettings[]>('/api/settings/categorie');
  return data;
}

export async function updateCategoriaSettings(
  categoria: string,
  body: { tariffa_eur_kg?: number; tipo_operazione_default?: TipoOperazione; iva_default?: number; attiva?: boolean },
): Promise<CategoriaSettings> {
  const { data } = await apiClient.patch<CategoriaSettings>(`/api/settings/categorie/${categoria}`, body);
  return data;
}

// ── CER rifiuti prodotti (Blocco 8): sorgente CER output dello scarico ──────────
/** Comma dell'art. 74 DPR 633/72: il backend ammette 7 e 8. */
export type CommaReverseCharge = 7 | 8;

export interface CerRifiutoProdotto {
  id: string;
  cer: string;
  nome_descrittivo: string | null;
  tipologia: Tipologia | null;
  classificazione: Classificazione | null;
  /** V72 — DEFAULT con cui nasce la riga di una bozza vendita, non un vincolo. */
  regime_iva: RegimeIva;
  /**
   * V73 — comma dell'art. 74 DPR 633/72 che decide la dicitura di reverse
   * charge: 7 = ferrosi, vetro, gomma, plastica; 8 = metalli non ferrosi.
   * CONFIGURABILE e non dedotto dal CER (il silicio e' dubbio). Come
   * `regime_iva` e' un DEFAULT: la riga in bozza ne tiene lo snapshot.
   */
  comma_reverse_charge: CommaReverseCharge;
  attivo: boolean;
  created_at: string;
  updated_at: string;
}

export async function getCerRifiutiProdotti(soloAttivi = false): Promise<CerRifiutoProdotto[]> {
  const { data } = await apiClient.get<CerRifiutoProdotto[]>('/api/settings/cer-rifiuti-prodotti', {
    params: { solo_attivi: soloAttivi },
  });
  return data;
}

export async function createCerRifiutoProdotto(body: { cer: string; nome_descrittivo?: string | null; tipologia?: Tipologia | null; classificazione?: Classificazione | null; regime_iva?: RegimeIva; comma_reverse_charge?: CommaReverseCharge }): Promise<CerRifiutoProdotto> {
  const { data } = await apiClient.post<CerRifiutoProdotto>('/api/settings/cer-rifiuti-prodotti', body);
  return data;
}

export async function updateCerRifiutoProdotto(id: string, body: { cer?: string; nome_descrittivo?: string | null; tipologia?: Tipologia | null; classificazione?: Classificazione | null; regime_iva?: RegimeIva; comma_reverse_charge?: CommaReverseCharge; attivo?: boolean }): Promise<CerRifiutoProdotto> {
  const { data } = await apiClient.patch<CerRifiutoProdotto>(`/api/settings/cer-rifiuti-prodotti/${id}`, body);
  return data;
}

// ── Tipologie del rifiuto prodotto (V74) ───────────────────────────────────────
// ASSE DIVERSO dal «materiale prodotto»: quello e' il nome dell'uscita
// dell'impianto, questa e' la DEFINIZIONE commerciale di cio' che c'e' nel
// sacco e dipende da cosa e' stato trattato a monte. Alluminio FV e alluminio
// infissi hanno stesso CER e stessa uscita, e si vendono a prezzi diversi.
export interface TipologiaRifiutoProdotto {
  id: string;
  cer_rifiuto_id: string;
  cer: string;                      // dal registro referenziato (display)
  nome_descrittivo: string | null;  // idem
  nome: string;
  attivo: boolean;
  /** Listino di vendita, EUR/kg (V78). `null` = nessun default: la riga di
   *  fattura nasce senza prezzo e lo si digita in bozza. */
  prezzo_default: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Tipologie del catalogo. I due filtri rispondono a DUE domande diverse e non
 * sono intercambiabili (rilievo 4, PR #48):
 *  - `cer` = CODICE del rifiuto: «quali tipologie puo' portare un sacco di
 *    questo CER». E' la regola del backend (`_valida_tipologia`), e attraversa
 *    le righe di catalogo — un codice puo' stare su due righe (V47). E' questa
 *    che serve a chi registra o assegna una tipologia.
 *  - `cerRifiutoId` = la singola RIGA di catalogo: serve ad amministrare il
 *    catalogo, non a decidere cosa puo' finire su un big bag.
 */
export async function getTipologieRifiutoProdotto(
  soloAttive = false, filtro?: { cer?: string; cerRifiutoId?: string },
): Promise<TipologiaRifiutoProdotto[]> {
  const { data } = await apiClient.get<TipologiaRifiutoProdotto[]>(
    '/api/settings/tipologie-rifiuto-prodotto',
    { params: {
      solo_attive: soloAttive,
      ...(filtro?.cer ? { cer: filtro.cer } : {}),
      ...(filtro?.cerRifiutoId ? { cer_rifiuto_id: filtro.cerRifiutoId } : {}),
    } });
  return data;
}

export async function createTipologiaRifiutoProdotto(
  body: { nome: string; cer_rifiuto_id: string; prezzo_default?: number },
): Promise<TipologiaRifiutoProdotto> {
  const { data } = await apiClient.post<TipologiaRifiutoProdotto>(
    '/api/settings/tipologie-rifiuto-prodotto', body);
  return data;
}

export async function updateTipologiaRifiutoProdotto(
  id: string,
  body: { nome?: string; cer_rifiuto_id?: string; attivo?: boolean; prezzo_default?: number },
): Promise<TipologiaRifiutoProdotto> {
  const { data } = await apiClient.patch<TipologiaRifiutoProdotto>(
    `/api/settings/tipologie-rifiuto-prodotto/${id}`, body);
  return data;
}

// ── Uscite materiale lavorato (#131): N uscite : 1 CER rifiuto prodotto ─────────
export interface UscitaMaterialeLavorato {
  id: string;
  nome: string;
  cer_rifiuto_id: string;       // FK → CER rifiuto prodotto (V47)
  cer: string;                  // dal registro referenziato (display)
  nome_descrittivo: string | null;  // dal registro referenziato (display)
  attivo: boolean;
  created_at: string;
  updated_at: string;
}

export async function getUsciteMaterialeLavorato(soloAttivi = false): Promise<UscitaMaterialeLavorato[]> {
  const { data } = await apiClient.get<UscitaMaterialeLavorato[]>('/api/settings/uscite-materiale-lavorato', {
    params: { solo_attivi: soloAttivi },
  });
  return data;
}

export async function createUscitaMaterialeLavorato(body: { nome: string; cer_rifiuto_id: string }): Promise<UscitaMaterialeLavorato> {
  const { data } = await apiClient.post<UscitaMaterialeLavorato>('/api/settings/uscite-materiale-lavorato', body);
  return data;
}

export async function updateUscitaMaterialeLavorato(id: string, body: { nome?: string; cer_rifiuto_id?: string; attivo?: boolean }): Promise<UscitaMaterialeLavorato> {
  const { data } = await apiClient.patch<UscitaMaterialeLavorato>(`/api/settings/uscite-materiale-lavorato/${id}`, body);
  return data;
}
